import { useState, useRef, useEffect } from 'react'
import { LogOut, Building2, ChevronDown, Check } from 'lucide-react'
import { useAuthStore, AziendaInfo } from '../../core/stores/authStore'

interface MenuUserInfoProps {
  user: { email?: string; username?: string; azienda_nome?: string; azienda_codice?: string } | null
  onLogout: () => void
  compact?: boolean
}

export default function MenuUserInfo({ user, onLogout, compact }: MenuUserInfoProps) {
  const { aziende, selectAzienda, user: fullUser } = useAuthStore()
  const [showSwitcher, setShowSwitcher] = useState(false)
  const [switching, setSwitching] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentAziendaId = fullUser?.azienda_id
  const currentAziendaNome = fullUser?.azienda_nome
  const hasMultipleAziende = aziende.length > 1

  // Chiudi dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowSwitcher(false)
      }
    }
    if (showSwitcher) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSwitcher])

  const handleSwitch = async (az: AziendaInfo) => {
    if (az.id === currentAziendaId) {
      setShowSwitcher(false)
      return
    }
    setSwitching(true)
    const success = await selectAzienda(az.id)
    setSwitching(false)
    if (success) {
      setShowSwitcher(false)
      // Reload per aggiornare tutti i dati con la nuova azienda
      window.location.href = '/app'
    }
  }

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--primary-600)' }}>
          <span className="text-sm font-medium text-white">
            {user?.email?.charAt(0).toUpperCase()}
          </span>
        </div>
        <button
          onClick={onLogout}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-muted)' }}
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {/* Badge azienda corrente + switch */}
      {currentAziendaNome && (
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => hasMultipleAziende && setShowSwitcher(!showSwitcher)}
            className={`
              w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
              ${hasMultipleAziende ? 'cursor-pointer hover:bg-white/10' : 'cursor-default'}
            `}
            style={{
              backgroundColor: 'var(--primary-600)',
              color: 'white',
              opacity: 0.9,
            }}
            title={hasMultipleAziende ? 'Cambia azienda' : currentAziendaNome}
          >
            <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate flex-1 text-left">{currentAziendaNome}</span>
            {hasMultipleAziende && (
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showSwitcher ? 'rotate-180' : ''}`} />
            )}
          </button>

          {/* Dropdown switch azienda */}
          {showSwitcher && (
            <div
              className="absolute bottom-full left-0 right-0 mb-1 rounded-lg shadow-xl border overflow-hidden z-50"
              style={{
                backgroundColor: 'var(--bg-card, white)',
                borderColor: 'var(--border-color, #e5e7eb)',
              }}
            >
              <div className="px-3 py-2 border-b text-xs font-medium"
                style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color, #e5e7eb)' }}>
                Cambia azienda
              </div>
              {aziende.map((az) => (
                <button
                  key={az.id}
                  onClick={() => handleSwitch(az)}
                  disabled={switching}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <div
                    className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                    style={{ backgroundColor: az.id === currentAziendaId ? 'var(--primary-600)' : '#6b7280' }}
                  >
                    {az.codice.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-sm">{az.nome}</p>
                    <p className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{az.role}</p>
                  </div>
                  {az.id === currentAziendaId && (
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--primary-600)' }} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* User info */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--primary-600)' }}>
          <span className="font-medium text-white">
            {user?.email?.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text-on-sidebar)' }}>
            {user?.username}
          </p>
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {user?.email}
          </p>
        </div>
        <button
          onClick={onLogout}
          className="p-2 transition-colors hover:text-red-400"
          style={{ color: 'var(--text-muted)' }}
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
